/**
 * Tenant Selection Component
 *
 * Purpose: Allows users to select their active tenant after successful authentication
 *
 * Architecture: Modal component that displays available tenants and handles selection
 *
 * Dependencies: React, Material-UI, AuthContext
 * Author: Development Team
 * Created: 2025-01-27
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Card,
  CardContent,
  CardActionArea,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Business } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { AvailableTenant } from '../../types/auth';
import { SecondaryButton } from '../common/ActionButtons';

interface TenantSelectionProps {
  open: boolean;
  onClose: () => void;
  onTenantSelect?: (tenantId: string) => void; // Optional callback for custom navigation
}

/**
 * Tenant Selection Component
 *
 * Purpose: Displays available tenants and allows user to select one
 * Logic: Shows list of available tenants, handles selection, navigates to dashboard
 * Input: open - Whether modal is open
 *        onClose - Function to close modal
 * Output: JSX element for tenant selection modal
 *
 * @param open - Whether modal is open
 * @param onClose - Function to close modal
 * @returns JSX element for tenant selection modal
 */
/** Timeout in ms after which we reset isSelecting if the request hangs (e.g. stale session/cache) */
const SELECTION_TIMEOUT_MS = 15000;

const TenantSelection: React.FC<TenantSelectionProps> = ({ open, onClose, onTenantSelect }) => {
  const navigate = useNavigate();
  const { state, selectTenant } = useAuth();
  const [selectedTenant, setSelectedTenant] = useState<string | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selectionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Get available tenants from user data
  const availableTenants = state.user?.availableTenants || [];

  // Clear selection timeout on unmount or when modal closes
  useEffect(() => {
    if (!open) {
      if (selectionTimeoutRef.current) {
        clearTimeout(selectionTimeoutRef.current);
        selectionTimeoutRef.current = null;
      }
    }
    return () => {
      if (selectionTimeoutRef.current) {
        clearTimeout(selectionTimeoutRef.current);
      }
    };
  }, [open]);

  /**
   * Handles tenant selection
   *
   * Purpose: Selects a tenant and navigates to dashboard
   * Logic: Calls selectTenant API, handles success/error, navigates on success
   * Input: tenant_id - ID of tenant to select
   * Output: None (side effect: tenant selection and navigation)
   *
   * @param tenant_id - ID of tenant to select
   */
  const handleTenantSelection = async (tenant_id: string): Promise<void> => {
    if (isSelecting) return;

    setIsSelecting(true);
    setError(null);
    setSelectedTenant(tenant_id);

    // Guard against stuck "selecting" state (e.g. hung request after stale session/cache)
    if (selectionTimeoutRef.current) clearTimeout(selectionTimeoutRef.current);
    selectionTimeoutRef.current = setTimeout(() => {
      selectionTimeoutRef.current = null;
      setIsSelecting(false);
      setSelectedTenant(null);
      setError('Selection timed out. Please try again or refresh the page.');
    }, SELECTION_TIMEOUT_MS);

    try {
      await selectTenant(tenant_id);
      if (selectionTimeoutRef.current) {
        clearTimeout(selectionTimeoutRef.current);
        selectionTimeoutRef.current = null;
      }
      // Use custom navigation callback if provided, otherwise navigate to home
      if (onTenantSelect) {
        onTenantSelect(tenant_id);
      } else {
        // Default behavior: navigate to home on successful tenant selection
        navigate('/home', { replace: true });
      }
      onClose();
    } catch (err: any) {
      if (selectionTimeoutRef.current) {
        clearTimeout(selectionTimeoutRef.current);
        selectionTimeoutRef.current = null;
      }
      console.error('Tenant selection failed:', err);
      const errorMessage =
        err?.response?.data?.detail?.message ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to select tenant. Please try again.';
      setError(errorMessage);
      setSelectedTenant(null);
    } finally {
      setIsSelecting(false);
    }
  };

  /**
   * Formats last access time
   *
   * Purpose: Provides user-friendly display of last access time
   * Logic: Formats date string to relative time
   * Input: last_access - ISO date string
   * Output: string - Formatted time string
   *
   * @param last_access - ISO date string
   * @returns Formatted time string
   */
  const formatLastAccess = (last_access?: string): string => {
    if (!last_access) return 'Never';

    const lastAccess = new Date(last_access);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - lastAccess.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
  };

  // Keep modal above third-party overlays (e.g. Google Sign-In). Backdrop is
  // pointer-events: none so it never intercepts clicks; only the dialog panel
  // receives them (avoids greyed-out/unclickable modal in some sessions).
  const modalZIndex = 13000;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableEnforceFocus
      sx={{ zIndex: modalZIndex }}
      slotProps={{
        backdrop: {
          sx: {
            zIndex: modalZIndex,
            pointerEvents: 'none',
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: 400,
          position: 'relative',
          zIndex: modalZIndex + 1,
          pointerEvents: 'auto',
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h5" component="span" display="block" gutterBottom>
          Select Your Organization
        </Typography>
        <Typography variant="body2" color="textSecondary" component="span" display="block">
          Choose which organization you'd like to access
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {availableTenants.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Business sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="textSecondary" gutterBottom>
              No Organizations Available
            </Typography>
            <Typography variant="body2" color="textSecondary">
              You don't have access to any organizations yet. Please contact your administrator.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {availableTenants.map((tenant: AvailableTenant) => (
              <Card
                key={tenant.tenant_id}
                data-testid={`tenant-card-${tenant.tenant_id}`}
                variant="outlined"
                sx={{
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: 2,
                    transform: 'translateY(-1px)',
                  },
                  ...(tenant.tenant_id === 'GLOBAL' && {
                    border: '2px solid #f44336',
                    backgroundColor: '#fff5f5',
                  }),
                }}
              >
                <CardActionArea
                  onClick={() => handleTenantSelection(tenant.tenant_id)}
                  disabled={isSelecting}
                  sx={{ p: 2 }}
                >
                  <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          <Business />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" component="h3">
                            {tenant.name}
                            {tenant.tenant_id === 'GLOBAL' && (
                              <Chip
                                label="GLOBAL"
                                size="small"
                                color="error"
                                sx={{ ml: 1, fontSize: '0.7rem' }}
                              />
                            )}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Last accessed: {formatLastAccess(tenant.last_access)}
                          </Typography>
                        </Box>
                      </Box>

                      <Box display="flex" alignItems="center" gap={1}>
                        {(tenant.roles ?? []).map((role) => (
                          <Chip
                            key={role}
                            label={role}
                            size="small"
                            data-testid="tenant-role-chip"
                          />
                        ))}
                        {isSelecting && selectedTenant === tenant.tenant_id && (
                          <CircularProgress size={20} />
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <SecondaryButton onClick={onClose} disabled={isSelecting}>
          Cancel
        </SecondaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default TenantSelection;
